import { ActingUserDto, UserDto } from '@act/shared/data-transfer-objects';
import { shouldSuggestBulkMemberTransfer } from './shouldSuggestBulkMemberTransfer.util';

/** Originating guide option available for a bulk transfer. */
export interface BulkTransferGuideOption {
  guideId: string;
  guideName: string;
}

/**
 * Returns unique guides with at least one transferable member on the current page.
 *
 * @param members - Members on the current table page.
 * @param guideNameById - Guide display names keyed by id.
 * @param actingUser - Current user.
 * @returns Sorted originating-guide options.
 */
export function getEligibleBulkTransferGuides(
  members: UserDto[],
  guideNameById: Record<string, string | undefined>,
  actingUser: ActingUserDto | null | undefined
): BulkTransferGuideOption[] {
  const guideIds = new Set<string>();

  for (const member of members) {
    if (shouldSuggestBulkMemberTransfer(member, actingUser)) {
      guideIds.add(member.assignedGuideId);
    }
  }

  return [...guideIds]
    .map(guideId => ({
      guideId,
      guideName: guideNameById[guideId] ?? 'Unknown guide',
    }))
    .sort((left, right) => left.guideName.localeCompare(right.guideName));
}

/**
 * Returns whether the table-level bulk transfer menu can be opened.
 *
 * @param members - Members on the current table page.
 * @param guideNameById - Guide display names keyed by id.
 * @param actingUser - Current user.
 * @returns True when the page contains a transferable guided member.
 */
export function canOpenTableBulkTransfer(
  members: UserDto[],
  guideNameById: Record<string, string | undefined>,
  actingUser: ActingUserDto | null | undefined
): boolean {
  return (
    getEligibleBulkTransferGuides(members, guideNameById, actingUser).length > 0
  );
}
